import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { apiRequest } from "../utils/api";

const getStorageKey = (phone) => `recent_searches_${phone || "guest"}`;

export default function useRecentSearches() {
  const { user } = useAuth();
  const phone = user?.phone || "";
  const storageKey = getStorageKey(phone);

  const [recentSearches, setRecentSearches] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Sync from backend on login
  useEffect(() => {
    if (phone && user?.recentSearches) {
      // If the backend has recent searches, use them to populate local storage
      // To avoid race conditions, we'll only override if we don't have local searches 
      // or if we trust backend more (the spec says "fetch from backend, populate localStorage")
      setRecentSearches(user.recentSearches);
      localStorage.setItem(storageKey, JSON.stringify(user.recentSearches));
    }
  }, [phone, user?.recentSearches, storageKey]);

  const addRecentSearch = useCallback(
    async (searchParams) => {
      if (!searchParams.from || !searchParams.to) return;

      const { from, to, date, seatType, category } = searchParams;
      const newSearch = {
        from,
        to,
        date,
        seatType,
        category,
        timestamp: new Date().toISOString(),
        tripCount: 1,
      };

      setRecentSearches((prev) => {
        const deduped = [...prev];
        const key = `${from.value || from}_${to.value || to}`.toLowerCase();
        const existingIdx = deduped.findIndex(
          (s) => `${s.from.value || s.from}_${s.to.value || s.to}`.toLowerCase() === key
        );

        if (existingIdx >= 0) {
          const existing = deduped[existingIdx];
          newSearch.tripCount = (existing.tripCount || 1) + 1;
          deduped.splice(existingIdx, 1);
        }

        deduped.unshift(newSearch);
        const finalSearches = deduped.slice(0, 10);

        localStorage.setItem(storageKey, JSON.stringify(finalSearches));

        // Sync to backend silently
        if (phone) {
          apiRequest("/users/recent-searches", {
            method: "PATCH",
            body: { recentSearches: finalSearches },
          }).catch((err) => console.error("Failed to sync recent searches", err));
        }

        return finalSearches;
      });
    },
    [phone, storageKey]
  );

  return { recentSearches, addRecentSearch };
}
